import { KCC20_SNAPSHOT_ARTIFACT_PINS } from '@kaspacom/kcc20-tx-builder';
import {
  EVM_COVERAGE_HASH,
  EVM_COVERAGE_TEXT,
  EVM_PREVIEW_FIELDS,
} from './evm-snapshot.mock';

/**
 * In-repo fixtures for the kcc20 mock interceptor. These let every page render
 * fully without a backend. Prices are sompi integers (1 KAS = 1e8 sompi) so the
 * real formatters apply. Illustrative only — never served in production.
 *
 * To migrate an endpoint to the real API, remove its branch from
 * {@link resolveMockResponse} (the interceptor then passes it through).
 */

const COV = 'fd73bd9efd0544c0f2085bf901ecd6bf7ef03f38029e421af736f6206e0fb3d1';
const MARKET =
  '91d4b7c0a1c5e2f309684b7d215c8a2f10e6c4b903f7a18d2c5e09b4d7a6f3c2';
const SECOND_COV =
  'aa11bb22cc33dd44ee55ff66007788990011223344556677889900aabbccddee';
const SECOND_MARKET =
  '5c8a2f10e6c4b903f7a18d2c5e09b4d7a6f3c291d4b7c0a1c5e2f309684b7d21';

const NOW = Date.now();
const HOUR = 3_600_000;
const MIGRATION_ID = 'migration-kasper-tn10-2026';
const MIGRATION_SLUG = 'kasper-tn10-2026';
const MIGRATION_SOURCE_NETWORK = 'kaspa-mainnet';
const MIGRATION_KRC_DEPLOY_ID = 'b68a0d63d59f4169a6e26b1a3a8f0911';
const MANIFEST_HASH =
  '9d80bc6f0820a9d6a9176239d7e8b55bc7f11e130de58d33d900d8e6121ce423';
const SOURCE_COMMITMENTS_HASH =
  'a613754865050e7b6005fefdc48a454609cdf348610d3d9d7d0a2e9958461294';
const VERIFIER_INSTRUCTIONS_HASH =
  '68db68d0011710660d035042b353b8ffc5f0775fe1bf677b043e0304ff22ade5';
const MIGRATION_WRAPPER_ID =
  'd72a7f0c1e5b4a0f96c1c4a8ee6d29b5b74621dc3c6a38e2e9c31f5a3d0f2c11';
const MIGRATION_ORDERBOOK_ID =
  'e18b8f9d2c6a41d0b57a3c2f5e609ab17c3f4d2a1e0b6c59f8d7a2c1b0e9f6d3';
const MIGRATION_CHECKPOINT_LABEL = 'daa-score:248500000';
const MIGRATION_CHECKPOINT = {
  blockHash: '5b8d6b2cbb98a88c3b3d865dc5a6f7d70dc18fead0c5a7e516ba4b57e4c4d7c2',
  daaScore: '248500000',
  krc20OpScore: '1890042',
  krc20StateCheckpoint: 'krc20-state:kasper:248500000',
  lastAcceptedTxid:
    '3d41a7d3f2f1c8d77ab3b8dc8be6f2d9e64696e6f51c2b35174405f2c4fd8a11',
  muhash: 'c65e6a2b11d8892103fbcb1f1cbe8e0f62874645626c160f81717d12a46e2f43',
};

const MIGRATION_SOURCES = [
  {
    id: 'kasplex-api',
    sourceId: 'kasplex-api',
    operator: 'Kasplex',
    trustDomain: 'kasplex-public-indexer',
    adapterVersion: 'kasplex-oplist-replay-v1',
    network: MIGRATION_SOURCE_NETWORK,
    krcDeployId: MIGRATION_KRC_DEPLOY_ID,
    checkpointLabel: MIGRATION_CHECKPOINT_LABEL,
    checkpoint: MIGRATION_CHECKPOINT,
    artifactHash:
      'sha256:9f9c6c38c65a0d4e8e6d9d28b75918f09e060e8f62455f93511f4a735ac1ac8b',
    holderCount: 2048,
    totalAmount: '4200000000000000',
  },
];

const MIGRATION_SHARDS = [0, 1, 2, 3].map((index) => ({
  id: `shard-${index}`,
  index,
  root: `${(index + 10).toString(16)}`.repeat(64).slice(0, 64),
  holderCount: 512,
  allocationAmount: '1050000000000000',
  claimedAmount: index < 2 ? '820000000000000' : '700000000000000',
  reserveAmount: index < 2 ? '230000000000000' : '350000000000000',
  status: 'ready',
}));

const MIGRATION_ARTIFACTS = [
  {
    id: 'manifest',
    label: 'Canonical manifest',
    hash: `sha256:${MANIFEST_HASH}`,
    downloadUrl: `/migrations/${MIGRATION_SLUG}/artifacts/${MANIFEST_HASH}`,
    mediaType: 'application/json',
    verifierUrl: 'https://mock.kcc20.local/verify/manifest',
  },
  {
    id: 'sources',
    label: 'Source commitments',
    hash: `sha256:${SOURCE_COMMITMENTS_HASH}`,
    downloadUrl: `/migrations/${MIGRATION_SLUG}/artifacts/${SOURCE_COMMITMENTS_HASH}`,
    mediaType: 'application/json',
    verifierUrl: 'https://mock.kcc20.local/verify/sources',
  },
  {
    id: 'verifier',
    label: 'Verifier instructions',
    hash: `sha256:${VERIFIER_INSTRUCTIONS_HASH}`,
    downloadUrl: `/migrations/${MIGRATION_SLUG}/artifacts/${VERIFIER_INSTRUCTIONS_HASH}`,
    mediaType: 'text/plain',
    verifierUrl: 'https://mock.kcc20.local/verify/instructions',
  },
];

const MIGRATION_ARTIFACT_POLICY = {
  rulesVersion: 'snapshot-rules-v1',
  shardPolicyVersion: 'snapshot-shards-v1',
  maxEntriesPerShard: 10000,
  kcc20Standard: 'KCC-0001',
  silverscriptCompiler: 'silverscript-v1.0.0',
  kcc20ArtifactSha256:
    'de003e4cfda11ac89d29a1563429bea2ab2cac49115c164b1c45ebf814a62842',
  bootstrapArtifactSha256:
    KCC20_SNAPSHOT_ARTIFACT_PINS.bootstrap.artifactSha256,
  controllerArtifactSha256:
    KCC20_SNAPSHOT_ARTIFACT_PINS.controller.artifactSha256,
  inclusionPolicySha256:
    'edb3f3c5963150bf0b1a2f3d9dc43b7cc497a330287ad32c3f88042f2cdb3d44',
  exclusionsSha256:
    '03ab1134ab8be65f212fe8c4b328a7e0ed04b5dd5decc984060bb07dd92bca58',
  benchmarkEvidenceSha256:
    '50b1338883f0c6d889cf0c2f2517d07417103a1385d0903f3e3a7e0cc9df4067',
  claimNotBeforeDaaScore: '248600000',
};

const MIGRATION_SUMMARY = {
  id: MIGRATION_ID,
  slug: MIGRATION_SLUG,
  status: 'LIVE',
  sourceNetwork: MIGRATION_SOURCE_NETWORK,
  sourceTokenName: 'Kasper',
  sourceTicker: 'KASPER',
  krcDeployId: MIGRATION_KRC_DEPLOY_ID,
  checkpoint: MIGRATION_CHECKPOINT_LABEL,
  manifestHash: MANIFEST_HASH,
  token: {
    covenantId: COV,
    ticker: 'KASPER',
    name: 'Kasper',
    decimals: 8,
  },
  conversion: {
    sourceDecimals: 8,
    targetDecimals: 8,
    numerator: '1',
    denominator: '1',
    display: '1 KRC20 base unit = 1 KCC20 base unit',
  },
  progress: {
    holderCount: 2048,
    claimedHolderCount: 1482,
    totalAmount: '4200000000000000',
    claimedAmount: '3040000000000000',
  },
  artifactPolicy: MIGRATION_ARTIFACT_POLICY,
};

const MIGRATION_DETAIL = {
  ...MIGRATION_SUMMARY,
  bootstrap: { phase: 'STARTED' },
  disclosure:
    'This is a one-time social-consensus snapshot. The source KRC20 remains active, and transfers after the recorded checkpoint do not change this distribution.',
  publishedAt: new Date(NOW - 3 * 24 * HOUR).toISOString(),
  sources: MIGRATION_SOURCES,
  artifacts: MIGRATION_ARTIFACTS,
  shards: MIGRATION_SHARDS,
  tradingSetup: {
    status: 'READY',
    wrapperId: MIGRATION_WRAPPER_ID,
    orderbookId: MIGRATION_ORDERBOOK_ID,
    checkedAt: new Date(NOW - 8 * 60_000).toISOString(),
  },
  writeCapabilities: { claimBuild: false },
};

const MIGRATION_ADMIN_DETAIL = {
  ...MIGRATION_SUMMARY,
  status: 'VERIFYING',
  updatedAt: new Date(NOW - 12 * 60_000).toISOString(),
  createdByOwner:
    '1111111111111111111111111111111111111111111111111111111111111111',
  blockedReason:
    'Wallet operations stay disabled in this mock until backend orchestration contracts are available.',
  bootstrap: { phase: 'STARTED' },
  sources: MIGRATION_SOURCES,
  artifacts: MIGRATION_ARTIFACTS,
  shards: MIGRATION_SHARDS,
  tradingSetup: {
    status: 'PENDING',
    wrapperId: MIGRATION_WRAPPER_ID,
    orderbookId: MIGRATION_ORDERBOOK_ID,
    checkedAt: new Date(NOW - 8 * 60_000).toISOString(),
  },
  workflow: [
    [
      'identity',
      'Identity and policy',
      'complete',
      'Network, ratio, checkpoint, and address policy recorded.',
    ],
    [
      'sources',
      'Checkpoint sources',
      'complete',
      'Kasplex operation replay artifact captured.',
    ],
    [
      'reconciliation',
      'Reconciliation',
      'complete',
      'Holder and total comparisons agree.',
    ],
    [
      'manifest',
      'Manifest and shards',
      'complete',
      'Canonical bytes and four shard roots frozen.',
    ],
    [
      'approval',
      'Approval',
      'complete',
      'A distinct administrator approved the manifest.',
    ],
    [
      'deployment',
      'Deployment',
      'current',
      'The signerless start outputs are being verified.',
    ],
    [
      'trading',
      'Trading setup',
      'pending',
      'Wrapper and orderbook setup waits for verification.',
    ],
    [
      'publication',
      'Publication',
      'pending',
      'Public discovery remains blocked until every gate passes.',
    ],
  ].map(([key, label, status, detail]) => ({ key, label, status, detail })),
  readiness: [
    {
      key: 'token',
      label: 'Token genesis',
      status: 'passed',
      detail: 'Fixed supply created atomically by snapshot start.',
    },
    {
      key: 'controllers',
      label: 'Controllers',
      status: 'passed',
      detail: 'Four controller genesis outputs indexed.',
    },
    {
      key: 'reserves',
      label: 'Snapshot reserves',
      status: 'pending',
      detail: 'Waiting for all start outputs to be reconciled.',
    },
    {
      key: 'supply',
      label: 'Supply reconciliation',
      status: 'pending',
      detail: 'Runs after every reserve indexes.',
    },
    {
      key: 'indexer',
      label: 'Indexer freshness',
      status: 'passed',
      detail: 'Canonical projection is current.',
    },
    {
      key: 'wrapper',
      label: 'Wrapper',
      status: 'pending',
      detail: 'Not queued before verification.',
    },
    {
      key: 'orderbook',
      label: 'Orderbook',
      status: 'pending',
      detail: 'No active market yet.',
    },
    {
      key: 'publication',
      label: 'Publication',
      status: 'blocked',
      detail: 'Readiness gates remain open.',
    },
  ],
  audit: [
    {
      id: 'audit-3',
      action: 'SNAPSHOT_START_INDEXED',
      actorOwner: 'system:indexer',
      occurredAt: new Date(NOW - 12 * 60_000).toISOString(),
      previousState: 'RESERVES_HANDING_OFF',
      nextState: 'VERIFYING',
    },
    {
      id: 'audit-2',
      action: 'MANIFEST_APPROVED',
      actorOwner:
        '2222222222222222222222222222222222222222222222222222222222222222',
      occurredAt: new Date(NOW - 4 * HOUR).toISOString(),
      previousState: 'SNAPSHOT_READY',
      nextState: 'APPROVED',
    },
  ],
  mismatchCount: 0,
  unsupportedOwnerCount: 0,
  exclusionCount: 3,
  reconciliationHash:
    '9f80bc6f0820a9d6a9176239d7e8b55bc7f11e130de58d33d900d8e6121ce423',
  approvedByOwner:
    '2222222222222222222222222222222222222222222222222222222222222222',
  approvedAt: new Date(NOW - 4 * HOUR).toISOString(),
  writeCapabilities: {
    create: false,
    update: false,
    claimBuild: false,
    deploymentNextBuild: false,
    approve: false,
    publish: false,
  },
  deploymentReceipts: [
    {
      operationId: 'deployment-controller-1',
      stage: 'controller-deploy',
      label: 'Snapshot controller genesis',
      status: 'indexed',
      txid: '7'.repeat(64),
      indexedAt: new Date(NOW - 2 * HOUR).toISOString(),
      explorerUrl: `https://tn10-covenants.kaspa.com/tx/${'7'.repeat(64)}`,
    },
    {
      operationId: 'deployment-start',
      stage: 'snapshot-start',
      label: 'Signerless snapshot start',
      status: 'submitted',
      shard: 2,
      txid: '8'.repeat(64),
      submittedAt: new Date(NOW - 12 * 60_000).toISOString(),
      explorerUrl: `https://tn10-covenants.kaspa.com/tx/${'8'.repeat(64)}`,
    },
  ],
};

function buildCandles(tokenIdHex: string): unknown[] {
  const n = 48;
  const base = 3_900_000;
  const out: unknown[] = [];
  let prevClose = base;
  for (let i = 0; i < n; i += 1) {
    const wave = Math.sin(i / 5) * 130_000 + i * 8500;
    const open = prevClose;
    const close = Math.round(base + wave);
    const high = Math.max(open, close) + 42_000 + (i % 4) * 8_000;
    const low = Math.min(open, close) - 36_000 - (i % 3) * 7_000;
    const volume =
      60_000_000_000 + Math.round(Math.abs(Math.sin(i / 3)) * 150_000_000_000);
    out.push({
      tokenIdHex,
      bucketTimeMs: NOW - (n - 1 - i) * HOUR,
      openUnitPriceSompi: String(open),
      highUnitPriceSompi: String(high),
      lowUnitPriceSompi: String(low),
      closeUnitPriceSompi: String(close),
      volumeSompi: String(volume),
      tokenVolume: String(Math.round(volume / 4_200_000)),
      tradeCount: 20 + (i % 11),
    });
    prevClose = close;
  }
  (out[out.length - 1] as { closeUnitPriceSompi: string }).closeUnitPriceSompi =
    '4280000';
  return out;
}

function buildOrderbook(tokenIdHex: string): unknown {
  const mk = (
    side: 'ask' | 'bid',
    i: number,
    price: number,
    amount: number,
  ) => ({
    orderId: `${side}-${tokenIdHex.slice(0, 6)}-${i}`,
    tokenIdHex,
    canonicalTokenIdHex: tokenIdHex === MARKET ? COV : tokenIdHex,
    wrapperIdHex: tokenIdHex,
    wrappedMarketIdHex: tokenIdHex,
    side,
    marketSide: side,
    status: 'open',
    templateName: side === 'ask' ? 'order.sale.v3' : 'order.bid.v3',
    tokenAmount: String(amount),
    remainingTokenAmount: String(amount),
    priceSompi: String(price * amount),
    unitPriceSompi: String(price),
    createdAtMs: NOW - i * 60_000,
  });
  const depth = (orders: Array<ReturnType<typeof mk>>) => {
    const byPrice = new Map<
      string,
      { tokenAmount: bigint; orderCount: number }
    >();
    for (const order of orders) {
      const existing = byPrice.get(order['unitPriceSompi']);
      byPrice.set(order['unitPriceSompi'], {
        tokenAmount:
          (existing?.tokenAmount ?? 0n) +
          BigInt(order['remainingTokenAmount'] || order['tokenAmount']),
        orderCount: (existing?.orderCount ?? 0) + 1,
      });
    }
    return [...byPrice.entries()]
      .map(([unitPriceSompi, level]) => ({
        unitPriceSompi,
        tokenAmount: level.tokenAmount.toString(),
        orderCount: level.orderCount,
      }))
      .sort(
        (left, right) =>
          Number(right.unitPriceSompi) - Number(left.unitPriceSompi),
      );
  };
  const asks = [
    mk('ask', 1, 4_360_000, 8_900),
    mk('ask', 2, 4_340_000, 21_200),
    mk('ask', 3, 4_340_000, 14_500),
    mk('ask', 4, 4_300_000, 26_750),
    mk('ask', 5, 4_290_000, 12_500),
  ];
  const bids = [
    mk('bid', 1, 4_270_000, 9_300),
    mk('bid', 2, 4_250_000, 22_100),
    mk('bid', 3, 4_250_000, 11_900),
    mk('bid', 4, 4_210_000, 18_400),
    mk('bid', 5, 4_180_000, 30_600),
  ];
  return {
    tokenIdHex,
    asks,
    bids,
    askDepth: depth(asks).reverse(),
    bidDepth: depth(bids),
  };
}

function buildActivity(tokenIdHex: string): unknown[] {
  const kinds: [string, string, string][] = [
    ['fill', 'buy', 'kcc20orderbook.fill-ask'],
    ['fill', 'sell', 'kcc20orderbook.fill-bid'],
    ['wrap', 'deposit', 'wrapper.wrap'],
    ['mint', 'mint', 'mint.public'],
    ['fill', 'buy', 'kcc20orderbook.fill-ask'],
    ['unwrap', 'withdraw', 'wrapper.unwrap'],
  ];
  return kinds.map((k, i) => ({
    tokenIdHex,
    txidHex: `${tokenIdHex.slice(0, 8)}${String(i).padStart(2, '0')}deadbeef`,
    orderId: i % 2 === 0 ? `ask-${tokenIdHex.slice(0, 6)}-${i}` : null,
    maker:
      'kaspatest:qr2fln7qyy70m63l206qhf9nf5qg35gkd5rjqpzry9x8gf2tvdw0s3jn54khc',
    kind: k[0],
    side: k[1],
    entrypoint: k[2],
    tokenAmount: String(2_000 + i * 5_180),
    priceSompi: String(4_280_000 * (2_000 + i * 5_180)),
    unitPriceSompi: String(4_290_000 - i * 5_000),
    blockTimeMs: NOW - i * 7 * 60_000,
  }));
}

function buildQuote(tokenIdHex: string): unknown {
  return {
    tokenIdHex,
    side: 'buy',
    requestedAmount: '12000',
    availableAmount: '24100',
    minFillAmount: '100',
    unitPriceSompi: '4290000',
    totalPriceSompi: '51480000000',
    protocolFeeSompi: '128700000',
    feeTicketEligible: true,
    feeTicketRootId: '0x3f7b9e3d',
    feeTicketDiscountBps: 25,
    feeTicketDiscountedProtocolFeeSompi: '96525000',
    valid: true,
    errors: [],
  };
}

function metadataFor(
  covenantId: string,
  name: string,
  ticker: string,
): unknown {
  return {
    covenantId,
    tokenIdHex: covenantId,
    name,
    ticker,
    decimals: 8,
    description: `${name} is a wrapped KCC20 market on Kaspa.`,
    websiteUrl: 'https://kaspa.com',
    xUrl: 'https://x.com/KaspaCurrency',
    source: 'backend',
    verificationStatus: 'verified',
    submittedByOwner:
      'kaspatest:qr2fln7qyy70m63l206qhf9nf5qg35gkd5rjqpzry9x8gf2tvdw0s3jn54khc',
  };
}

function marketFor(
  tokenIdHex: string,
  ask: number,
  bid: number,
  vol24h: string,
  name: string,
  ticker: string,
): unknown {
  return {
    tokenIdHex,
    bestAskUnitPriceSompi: String(ask),
    bestBidUnitPriceSompi: String(bid),
    spreadSompi: String(ask - bid),
    openAskCount: 156,
    openBidCount: 153,
    totalTrades: 18450,
    totalVolumeSompi: '184000000000000',
    volume24hSompi: vol24h,
    volume7dSompi: '129000000000000',
    tradeCount24h: 612,
    tradeCount7d: 4120,
    lastTradeTimeMs: NOW - 2 * 60_000,
    lastTradeTxidHex: `${tokenIdHex.slice(0, 8)}lasttrade`,
    metadata: metadataFor(tokenIdHex, name, ticker),
  };
}

function sortableMockMarketValue(
  market: Record<string, unknown>,
  sortBy: string,
): bigint {
  const value = market[sortBy];
  if (typeof value === 'number' && Number.isFinite(value)) {
    return BigInt(value);
  }
  if (typeof value === 'string' && /^(0|[1-9][0-9]*)$/.test(value)) {
    return BigInt(value);
  }
  return 0n;
}

const TOKEN_DETAIL = {
  covenantId: COV,
  ticker: 'KASPER',
  tokenName: 'Kasper',
  creatorOwner: 'kaspatest:qpr20ffcreator000000000000000000000000000',
  stateOwner: 'kaspatest:qpr20ffstate0000000000000000000000000000000',
  stateOwnerType: 1,
  treasuryRecipient: 'kaspatest:qpr20fftreasury00000000000000000000000000',
  protocolFeeRecipient: 'kaspatest:qpr20ffprotocol0000000000000000000000000',
  protocolFeeBps: 30,
  metadata: metadataFor(COV, 'Kasper', 'KASPER'),
  wrapper: {
    wrapperId: '0x7af3c9e21b8d04f6a1c5',
    marketId: MARKET,
    feeTicketSupported: true,
    enabled: true,
  },
  mintPolicy: {
    mintable: true,
    policy: 'public',
    policyValue: 2,
    remainingSupply: '620000000',
    mintPricePerTokenSompi: '90000',
    treasuryRecipient: 'kaspatest:qpr20fftreasury00000000000000000000000000',
    protocolFeeRecipient: 'kaspatest:qpr20ffprotocol0000000000000000000000000',
    protocolFeeBps: 30,
  },
  supply: {
    maxSupply: '1000000000',
    premintSupply: '200000000',
    mintedSupply: '380000000',
    remainingMintSupply: '620000000',
    canonicalSupply: '388000000',
    wrapperReserveSupply: '612000000',
    orderbookTradeableSupply: '612000000',
  },
  holderCount: 9242,
  topHolders: [
    {
      ownerIdentifier: '0x7a3f000000000000000000000000000000000000',
      identifierType: 0,
      amount: '48200000',
      utxoCount: 12,
    },
    {
      ownerIdentifier: '0x4e8d000000000000000000000000000000000000',
      identifierType: 0,
      amount: '31000000',
      utxoCount: 8,
    },
    {
      ownerIdentifier: '0x5d91000000000000000000000000000000000000',
      identifierType: 0,
      amount: '22400000',
      utxoCount: 5,
    },
    {
      ownerIdentifier: '0xc26e000000000000000000000000000000000000',
      identifierType: 0,
      amount: '18900000',
      utxoCount: 9,
    },
    {
      ownerIdentifier: '0x1b4a000000000000000000000000000000000000',
      identifierType: 0,
      amount: '14200000',
      utxoCount: 4,
    },
  ],
  marketCapFloorAskSompi: '4280000000000000',
};

const MARKETS = [
  marketFor(MARKET, 4_290_000, 4_270_000, '18400000000000', 'Kasper', 'KASPER'),
  marketFor(
    SECOND_MARKET,
    1_120_000,
    1_090_000,
    '7300000000000',
    'Nacho',
    'NACHO',
  ),
];

const FRESHNESS = {
  ok: true,
  stale: false,
  suggestedPollMs: 5000,
  scanner: {
    dbCursorLagBlocks: 1,
    tipBlueScore: 42891233,
    cursorBlueScore: 42891232,
    updatedAtMs: NOW - 1500,
  },
};

const STATS = {
  tokenIdHex: MARKET,
  windowMs: 86_400_000,
  tradeCount: 612,
  volumeSompi: '18400000000000',
  tokenVolume: '4290000',
  openUnitPriceSompi: '3990000',
  highUnitPriceSompi: '4410000',
  lowUnitPriceSompi: '3820000',
  closeUnitPriceSompi: '4280000',
  priceChangeSompi: '290000',
  openAskDepthSompi: '90000000000',
  openAskDepthTokens: '210000',
  openBidDepthSompi: '88000000000',
  openBidDepthTokens: '205000',
};

const PORTFOLIO_ROW = {
  canonicalTokenIdHex: COV,
  wrapperIdHex: MARKET,
  wrappedMarketIdHex: MARKET,
  nativeBalance: '388000000',
  orderbookBalance: '612000000',
  totalBalance: '1000000000',
  assetUtxoCount: 12,
  openAskAmount: '210000',
  openBidAmount: '205000',
  openOrderCount: 309,
  feeTicketSupported: true,
  lastChangeMs: NOW - 2 * 60_000,
  actions: {
    wrap: { enabled: true, reason: null },
    unwrap: { enabled: true, reason: null },
    trade: { enabled: true, reason: null },
  },
};

function tokenPageData(token = TOKEN_DETAIL): unknown {
  const wrapper = token.wrapper;
  return {
    token,
    wrappers: wrapper ? [wrapper] : [],
    actions: {
      mint: { enabled: true, reason: null },
      wrap: { enabled: true, reason: null },
      unwrap: { enabled: true, reason: null },
      trade: { enabled: true, reason: null },
      deployWrapper: { enabled: false, reason: 'wrapper already exists' },
    },
    debug: {
      invalidWrappers: [],
    },
  };
}

/**
 * Resolve a mock response body for a backend path (already stripped of the
 * apiBaseUrl + query string). Returns `undefined` when there is no fixture, so
 * the interceptor passes the request through to the real API.
 */
export function resolveMockResponse(
  method: string,
  path: string,
  params: URLSearchParams = new URLSearchParams(),
): unknown | undefined {
  if (method !== 'GET') {
    return undefined;
  }
  const hex = '[0-9a-fA-F]{64}';
  const m = (re: RegExp) => re.test(path);

  if (m(new RegExp(`^/tokens/${hex}/page-data$`))) {
    const token = path.includes(SECOND_COV)
      ? {
          ...TOKEN_DETAIL,
          covenantId: SECOND_COV,
          ticker: 'NACHO',
          tokenName: 'Nacho',
          wrapper: {
            wrapperId: '0x5c8a',
            marketId: SECOND_MARKET,
            feeTicketSupported: true,
            enabled: true,
          },
          metadata: metadataFor(SECOND_COV, 'Nacho', 'NACHO'),
        }
      : TOKEN_DETAIL;
    return tokenPageData(token);
  }
  if (m(new RegExp(`^/tokens/${hex}$`))) {
    return path.includes(SECOND_COV)
      ? {
          ...TOKEN_DETAIL,
          covenantId: SECOND_COV,
          ticker: 'NACHO',
          tokenName: 'Nacho',
          wrapper: {
            wrapperId: '0x5c8a',
            marketId: SECOND_MARKET,
            feeTicketSupported: true,
            enabled: true,
          },
          metadata: metadataFor(SECOND_COV, 'Nacho', 'NACHO'),
        }
      : TOKEN_DETAIL;
  }
  if (m(new RegExp(`^/metadata/${hex}/permissions$`))) {
    return {
      covenantId: COV,
      canEdit: true,
      reason: 'You are the token creator.',
    };
  }
  if (m(new RegExp(`^/metadata/${hex}$`))) {
    return path.includes(SECOND_COV)
      ? metadataFor(SECOND_COV, 'Nacho', 'NACHO')
      : metadataFor(COV, 'Kasper', 'KASPER');
  }
  if (path === '/migrations/igra-snapshot-preview') {
    return {
      ...MIGRATION_DETAIL,
      ...EVM_PREVIEW_FIELDS,
      id: 'igra-snapshot-preview',
      slug: 'igra-snapshot-preview',
      sourceTicker: 'L2TOKEN',
      sourceTokenName: 'Igra preview token',
      checkpoint: 'block:123456',
      disclosure:
        'This is a preview fixture. The source L2 token remains active; post-snapshot transfers do not change KCC20 claims.',
      conversion: {
        sourceDecimals: 18,
        targetDecimals: 8,
        numerator: '1',
        denominator: '1',
        display: '1 source token = 1 KCC20 token',
      },
      sources: MIGRATION_SOURCES.map((source, index) => ({
        ...source,
        source: EVM_PREVIEW_FIELDS.source,
        sourceCheckpoint: EVM_PREVIEW_FIELDS.sourceCheckpoint,
        adapterVersion:
          index === 0 ? 'token-graph-balances' : 'archive-transfer-replay',
      })),
      artifacts: [
        {
          id: 'coverage',
          label: 'Wallet coverage',
          hash: `sha256:${EVM_COVERAGE_HASH}`,
          downloadUrl:
            '/migrations/igra-snapshot-preview/artifacts/' + EVM_COVERAGE_HASH,
          mediaType: 'application/json',
        },
      ],
    };
  }
  if (
    path ===
    '/migrations/igra-snapshot-preview/artifacts/' + EVM_COVERAGE_HASH
  ) {
    return EVM_COVERAGE_TEXT;
  }
  if (path === '/migrations') {
    return { items: [MIGRATION_SUMMARY], total: 1 };
  }
  if (path === `/migrations/${MIGRATION_SLUG}`) {
    return MIGRATION_DETAIL;
  }
  if (path === `/migrations/${MIGRATION_SLUG}/artifacts`) {
    return MIGRATION_ARTIFACTS;
  }
  if (path === `/migrations/${MIGRATION_SLUG}/verifier-instructions`) {
    return {
      schema: 'kcc20-snapshot-verifier-instructions/v1',
      migrationId: MIGRATION_ID,
      slug: MIGRATION_SLUG,
      manifestHash: MANIFEST_HASH,
      tokenCovenantId: COV,
      disclosure: MIGRATION_DETAIL.disclosure,
      claimHistoryUrl: `/migrations/${MIGRATION_SLUG}/claims/history`,
      progressUrl: `/migrations/${MIGRATION_SLUG}/progress`,
      artifacts: MIGRATION_ARTIFACTS,
      contractPolicy: MIGRATION_ARTIFACT_POLICY,
      sourcePolicy: {
        requiredSourceCount: 1,
        requiredDistinctSourceIds: 1,
        requiredDistinctOperators: 1,
        requiredDistinctTrustDomains: 1,
        requiredDistinctArtifactHashes: 1,
        requiredCheckpointCount: 1,
        requiredAdapterCounts: {
          'kasplex-oplist-replay-v1': 1,
        },
      },
      sources: MIGRATION_SOURCES,
      deploymentTransactions: [],
      verificationSteps: [
        'Treat this as a one-time social-consensus migration: the source KRC20 remains active.',
        'Compare the KCC20 standard, SilverScript compiler, and contract artifact hashes to the verifier contractPolicy block.',
        'Require the Kasplex operation replay artifact captured through the fixed DAA score.',
        'Download every public artifact and verify its sha256 hash.',
      ],
    };
  }
  if (path === `/migrations/${MIGRATION_SLUG}/claims/me`) {
    return {
      status: 'eligible',
      owner: '025c33e4d6d1482a2b7eb9d3c59ab1838071a26b4ff4e60a140b02128782dd78',
      ownerScheme: 0,
      recipient:
        '025c33e4d6d1482a2b7eb9d3c59ab1838071a26b4ff4e60a140b02128782dd78',
      recipientOwnerScheme: 0,
      amount: '125000000000',
      shard: 2,
      reason: 'Unclaimed allocation found in the approved manifest.',
    };
  }
  if (path === '/trading/markets') {
    return MARKETS;
  }
  if (path === '/trading/markets/discovery') {
    const requestedTokenId = params.get('tokenId')?.trim().toLowerCase();
    const search = params.get('search')?.trim().toLowerCase();
    const limit = Math.max(1, Number(params.get('limit') ?? 30) || 30);
    const offset = Math.max(0, Number(params.get('offset') ?? 0) || 0);
    const sortBy = params.get('sortBy') ?? 'volume24hSompi';
    const sortDirection =
      params.get('sortDirection') === 'asc' ? 'asc' : 'desc';
    let items = [
      marketFor(
        MARKET,
        4_290_000,
        4_270_000,
        '18400000000000',
        'Kasper',
        'KASPER',
      ),
      marketFor(
        SECOND_MARKET,
        1_120_000,
        1_090_000,
        '7300000000000',
        'Nacho',
        'NACHO',
      ),
      marketFor(
        '11aa'.padEnd(64, '0'),
        320_000,
        312_000,
        '4100000000000',
        'Kango',
        'KANGO',
      ),
      marketFor(
        '22bb'.padEnd(64, '0'),
        88_000,
        84_500,
        '2600000000000',
        'Ghoad',
        'GHOAD',
      ),
      marketFor(
        '33cc'.padEnd(64, '0'),
        1_540_000,
        1_500_000,
        '1900000000000',
        'Kekw',
        'KEKW',
      ),
      marketFor(
        '44dd'.padEnd(64, '0'),
        47_000,
        45_200,
        '980000000000',
        'Zeal',
        'ZEAL',
      ),
    ];
    if (requestedTokenId) {
      items = items.filter(
        (item) =>
          (item as { tokenIdHex: string }).tokenIdHex.toLowerCase() ===
          requestedTokenId,
      );
    } else if (search) {
      items = items.filter((item) => {
        const market = item as {
          tokenIdHex: string;
          metadata?: { covenantId?: string; name?: string; ticker?: string };
        };
        return [
          market.tokenIdHex,
          market.metadata?.covenantId,
          market.metadata?.name,
          market.metadata?.ticker,
        ].some((value) => value?.toLowerCase().includes(search));
      });
    }
    items = items.sort((left, right) => {
      const leftMarket = left as Record<string, unknown>;
      const rightMarket = right as Record<string, unknown>;
      const leftValue = sortableMockMarketValue(leftMarket, sortBy);
      const rightValue = sortableMockMarketValue(rightMarket, sortBy);
      if (leftValue === rightValue) {
        return String(leftMarket['tokenIdHex']).localeCompare(
          String(rightMarket['tokenIdHex']),
        );
      }
      return sortDirection === 'asc'
        ? Number(leftValue - rightValue)
        : Number(rightValue - leftValue);
    });
    const paged = items.slice(offset, offset + limit);
    return {
      items: paged,
      total: items.length,
      limit,
      offset,
      sortBy,
      sortDirection,
      queryDurationMs: 42,
      freshness: {
        refreshedAtMs: NOW - 1500,
        sourceBlueScore: 42891233,
        rowCount: items.length,
      },
    };
  }
  if (path === '/trading/freshness') {
    return FRESHNESS;
  }
  const market = path.match(
    new RegExp(
      `^/trading/tokens/(${hex})/(orderbook|stats|candles|activity|quote)$`,
    ),
  );
  if (market) {
    const id = market[1];
    switch (market[2]) {
      case 'orderbook':
        return buildOrderbook(id);
      case 'stats':
        return { ...STATS, tokenIdHex: id };
      case 'candles':
        return buildCandles(id);
      case 'activity':
        return buildActivity(id);
      case 'quote':
        return buildQuote(id);
    }
  }
  if (m(new RegExp(`^/tokens/owners/${hex}/deployed$`))) {
    const limit = Math.max(1, Number(params.get('limit') ?? 10) || 10);
    const offset = Math.max(0, Number(params.get('offset') ?? 0) || 0);
    return {
      items: [TOKEN_DETAIL].slice(offset, offset + limit),
      total: 1,
      limit,
      offset,
      sortBy: params.get('sortBy') ?? 'ticker',
      sortDirection: params.get('sortDirection') ?? 'asc',
    };
  }
  if (m(new RegExp(`^/trading/owners/${hex}/orders$`))) {
    const limit = Math.max(1, Number(params.get('limit') ?? 10) || 10);
    const offset = Math.max(0, Number(params.get('offset') ?? 0) || 0);
    return {
      items: [],
      total: 0,
      limit,
      offset,
      sortBy: params.get('sortBy') ?? undefined,
      sortDirection: params.get('sortDirection') ?? undefined,
    };
  }
  if (m(new RegExp(`^/trading/owners/${hex}/history$`))) {
    const limit = Math.max(1, Number(params.get('limit') ?? 10) || 10);
    const offset = Math.max(0, Number(params.get('offset') ?? 0) || 0);
    return {
      items: [],
      total: 0,
      limit,
      offset,
      sortBy: params.get('sortBy') ?? undefined,
      sortDirection: params.get('sortDirection') ?? undefined,
    };
  }
  if (m(/^\/trading\/addresses\/.+\/positions$/)) {
    return [];
  }
  if (m(new RegExp(`^/wallet-operations/portfolio/tokens/${hex}/balance$`))) {
    return PORTFOLIO_ROW;
  }

  // ---- Token admin (manage page) -----------------------------------------
  if (m(new RegExp(`^/tokens/${hex}/wrappers$`))) {
    const id = path.includes(SECOND_COV) ? SECOND_MARKET : MARKET;
    return [
      {
        wrapperId: '0x7af3c9e21b8d04f6a1c5',
        marketId: id,
        feeTicketSupported: true,
        enabled: true,
      },
    ];
  }
  if (m(new RegExp(`^/admin/token-admin/${hex}/operations$`))) {
    return { items: [], total: 0 };
  }

  // ---- Rewards (FeeTickets + referrals) ----------------------------------
  if (
    path === '/rewards/me/fee-tickets/page-data' ||
    path === '/rewards/me/fee-tickets/history'
  ) {
    const owner = 'kaspatest:qpr20ffadmin00000000000000000000000000000000';
    const root = {
      configured: true,
      enabled: true,
      rootId: '0x3f7b9e3d2a1c8f04',
      rootOwner: owner,
      utilityTokenId: '0x7af3c9e21b8d04f6a1c5',
      utilityTokenAmount: '500',
      discountBps: 25,
      supportedMarketIds: Array.from(
        { length: 14 },
        (_, i) => `0x${(i + 1).toString(16).padStart(8, '0')}`,
      ),
    };
    const historyAll = [
      'create',
      'use',
      'use',
      'burn',
      'create',
      'use',
      'create',
      'use',
      'burn',
      'transfer',
      'use',
      'create',
      'use',
    ].map((type, i) => ({
      id: `ft-${i}`,
      type,
      source: 'tn10-indexer',
      status: 'indexed',
      action:
        type === 'create'
          ? 'feeticket.create'
          : type === 'burn'
            ? 'feeticket.burn'
            : type === 'transfer'
              ? 'feeticket.transfer'
              : 'kcc20orderbook.fill-ask',
      rootId: root.rootId,
      txidHex: `${type}${String(i).padStart(2, '0')}deadbeefcafe`,
      inputOutpoints: [],
      outputOutpoints: [],
      direction: type === 'transfer' ? 'sent' : undefined,
      counterpartyOwner: type === 'transfer' ? `${'9'.repeat(64)}` : undefined,
      ticketCount: type === 'transfer' ? 1 : undefined,
      sortTimeMs: NOW - i * 23 * 60_000,
      blockTimeMs: NOW - i * 23 * 60_000,
    }));
    const limit = Math.max(1, Number(params.get('limit') ?? 6) || 6);
    const offset = Math.max(0, Number(params.get('offset') ?? 0) || 0);
    const historyItems = historyAll.slice(offset, offset + limit);
    if (path === '/rewards/me/fee-tickets/history') {
      return {
        items: historyItems,
        total: historyAll.length,
        limit,
        offset,
        sortBy: params.get('sortBy') ?? 'sortTimeMs',
        sortDirection: params.get('sortDirection') ?? 'desc',
      };
    }
    const outpoints = Array.from({ length: 4 }, (_, i) => ({
      outpoint: `${i}:abcd${i}`,
      txidHex: `abcd${i}`.padEnd(16, '0'),
      vout: i,
      address: owner,
      amountSompi: '50000000',
      action: 'feeticket',
    }));
    return {
      owner,
      walletAddress: owner,
      root,
      denomination: {
        rootId: root.rootId,
        utilityTokenId: root.utilityTokenId,
        utilityTokenAmount: '500',
        discountBps: 25,
        supportedMarketIds: root.supportedMarketIds,
        singleTicketCollateralSompi: '200000000',
        batchTicketCollateralSompi: '200000000',
        batchCreateSupported: true,
        maxTicketBatchQuantity: 10,
      },
      balance: {
        owner,
        availableTickets: 12,
        createdTickets: 20,
        usedTickets: 6,
        burnedTickets: 2,
        sentTickets: 1,
        receivedTickets: 0,
        totalTickets: 12,
      },
      activeTicketOutpoints: { indexerAvailable: true, items: outpoints },
      pendingOperations: { items: [], total: 0, limit: 50, offset: 0 },
      referralRewards: {
        owner,
        walletAddress: owner,
        code: 'KASPER-9F4D',
        referredByOwner: undefined,
        canAddReferredBy: true,
        totalReferred: 23,
        last7d: 4,
        last30d: 17,
      },
      campaignPoints: { activeCampaign: null, points: [], totalPoints: null },
    };
  }

  // ---- Portfolio (account) -----------------------------------------------
  if (path === '/users/me/state') {
    return {
      owner: 'kaspatest:qpr20ffadmin00000000000000000000000000000000',
      watchlist: [
        COV,
        SECOND_COV,
        '11aa'.padEnd(64, '0'),
        '33cc'.padEnd(64, '0'),
      ],
      savedMarkets: [MARKET],
      preferences: {},
      drafts: [],
      notifications: {},
    };
  }
  if (m(/^\/trading\/addresses\/.+\/balances$/)) {
    const bal = (id: string, amount: string, minter = false) => ({
      tokenIdHex: id,
      ownerIdentifier: 'kaspatest:qpr20ffadmin',
      amount,
      identifierType: 0,
      identifierTypeName: 'address',
      isMinter: minter,
      utxoCount: 4,
      lastChangeMs: NOW - 60_000,
    });
    return [
      bal(COV, '142800', true),
      bal(SECOND_COV, '53000'),
      bal('11aa'.padEnd(64, '0'), '9100'),
    ];
  }
  if (path === '/wallet-operations/portfolio/assets') {
    return { items: [PORTFOLIO_ROW], total: 1, limit: 10, offset: 0 };
  }
  if (path === '/wallet-operations/portfolio/summary') {
    return {
      assetCount: 1,
      nativeAssetCount: 1,
      orderbookAssetCount: 1,
      openOrderAssetCount: 0,
      openOrderCount: 0,
      totalNativeBalance: PORTFOLIO_ROW.nativeBalance,
      totalOrderbookBalance: PORTFOLIO_ROW.orderbookBalance,
      totalBalance: PORTFOLIO_ROW.totalBalance,
      totalOpenAskAmount: PORTFOLIO_ROW.openAskAmount,
      totalOpenBidAmount: PORTFOLIO_ROW.openBidAmount,
      lastChangeMs: PORTFOLIO_ROW.lastChangeMs,
    };
  }
  if (m(/^\/trading\/owners\/.+\/orders$/)) {
    return { items: [], total: 0, limit: 10, offset: 0 };
  }
  if (m(/^\/trading\/owners\/.+\/trades$/)) {
    return { items: [], total: 0, limit: 10, offset: 0 };
  }
  if (m(/^\/trading\/owners\/.+\/history$/)) {
    return { items: [], total: 0, limit: 10, offset: 0 };
  }
  if (m(/^\/tokens\/owners\/.+\/deployed$/)) {
    return { items: [TOKEN_DETAIL], total: 1, limit: 10, offset: 0 };
  }
  if (path === '/wallet-operations/submissions') {
    return [];
  }
  if (
    path === '/wallet-operations/activity' ||
    path === '/wallet-operations/deposits-withdrawals'
  ) {
    return { items: [], total: 0, limit: 10, offset: 0 };
  }
  if (path === '/rewards/me/fee-tickets') {
    return {
      owner: 'kaspatest:qpr20ffadmin',
      availableTickets: 12,
      createdTickets: 20,
      usedTickets: 6,
      burnedTickets: 2,
      sentTickets: 1,
      receivedTickets: 0,
      totalTickets: 12,
    };
  }
  if (path === '/rewards/me/fee-tickets/available') {
    return {
      owner: 'kaspatest:qpr20ffadmin',
      walletAddress: 'kaspatest:qpr20ffadmin',
      availableTickets: 12,
      activeTicketOutpoints: {
        indexerAvailable: true,
        items: [],
      },
    };
  }
  if (path === '/rewards/fee-ticket-root') {
    return {
      configured: true,
      enabled: true,
      rootId: '0x3f7b9e3d2a1c8f04',
      utilityTokenId: '0x7af3c9e21b8d04f6a1c5',
      utilityTokenAmount: '500',
      discountBps: 25,
      singleTicketLockedKasSompi: '200000000',
      batchTicketLockedKasSompi: '200000000',
      supportedMarketIds: Array.from(
        { length: 14 },
        (_, i) => `0x${(i + 1).toString(16).padStart(8, '0')}`,
      ),
      batchCreateSupported: true,
      maxTicketBatchQuantity: 10,
    };
  }
  if (path === '/users/me/profile') {
    return {
      owner: 'kaspatest:qpr20ffadmin',
      walletAddress: 'kaspatest:qpr20ffadmin',
      emailVerified: false,
      notificationPreferences: {},
    };
  }
  if (path === '/referrals/me') {
    return {
      owner: 'kaspatest:qpr20ffadmin',
      code: 'KASPER-9F4D',
      totalReferred: 23,
    };
  }
  if (path === '/referrals/stats') {
    return { totalReferred: 23, last7d: 4, last30d: 17 };
  }

  // ---- Mints discovery ---------------------------------------------------
  if (path === '/tokens/mints/v2') {
    const mk = (
      name: string,
      ticker: string,
      cov: string,
      mode: string,
      status: string,
      minted: number,
      max: number,
      price: number,
      eligible: boolean,
      velocityAmount: number,
      createdAtMs: number,
      wrapper: unknown = null,
    ) => ({
      covenantId: cov,
      createdAtMs,
      ticker,
      tokenName: name,
      metadata: metadataFor(cov, name, ticker),
      mintPolicy: {
        ...TOKEN_DETAIL.mintPolicy,
        mintable: status === 'live',
        policy: mode,
        mintPricePerTokenSompi: String(price),
      },
      supply: {
        ...TOKEN_DETAIL.supply,
        maxSupply: String(max),
        mintedSupply: String(minted),
        remainingMintSupply: String(max - minted),
      },
      mintStatus: status,
      quickMintEligible: eligible,
      wrapper,
      marketCapFloorAskSompi: null,
      mintVelocity: {
        window: params.get('mintVelocityWindow') || '24h',
        windowMs: 86_400_000,
        mintedAmount: String(velocityAmount),
        mintedPercentBps: Math.floor((velocityAmount * 10_000) / max),
        mintCount: Math.max(0, Math.floor(velocityAmount / 10_000_000)),
        lastMintTimeMs: velocityAmount > 0 ? NOW - 12 * 60_000 : null,
      },
    });
    const items = [
      mk(
        'Kasper',
        'KASPER',
        COV,
        'public',
        'live',
        380_000_000,
        1_000_000_000,
        90_000,
        true,
        42_000_000,
        NOW - 12 * 60_000,
        TOKEN_DETAIL.wrapper,
      ),
      mk(
        'Nacho',
        'NACHO',
        SECOND_COV,
        'public',
        'live',
        120_000_000,
        500_000_000,
        45_000,
        true,
        90_000_000,
        NOW - 6 * 60_000,
      ),
      mk(
        'Kango',
        'KANGO',
        '11aa'.padEnd(64, '0'),
        'controlled',
        'live',
        60_000_000,
        200_000_000,
        120_000,
        false,
        8_000_000,
        NOW - 18 * 60_000,
      ),
      mk(
        'Ghoad',
        'GHOAD',
        '22bb'.padEnd(64, '0'),
        'public',
        'live',
        410_000_000,
        600_000_000,
        30_000,
        true,
        120_000_000,
        NOW - 3 * 60_000,
      ),
      mk(
        'Kekw',
        'KEKW',
        '33cc'.padEnd(64, '0'),
        'fixed',
        'ended',
        1_000_000_000,
        1_000_000_000,
        0,
        false,
        0,
        NOW - 48 * 60 * 60_000,
      ),
      mk(
        'Zeal',
        'ZEAL',
        '44dd'.padEnd(64, '0'),
        'public',
        'ended',
        250_000_000,
        250_000_000,
        75_000,
        false,
        0,
        NOW - 24 * 60 * 60_000,
      ),
    ];
    const mintPolicy = params.get('policy') || 'public';
    const mintStatus = params.get('mintStatus') || 'live';
    const wrapperStatus = params.get('wrapperStatus') || 'any';
    const sortBy = params.get('sortBy') || 'remainingMintSupply';
    const sortDirection = params.get('sortDirection') || 'desc';
    const limit = Number(params.get('limit') || 50);
    const offset = Number(params.get('offset') || 0);
    const numeric = (item: Record<string, unknown>): bigint | null => {
      const supply = item['supply'] as Record<string, unknown> | undefined;
      const policy = item['mintPolicy'] as Record<string, unknown> | undefined;
      const velocity = item['mintVelocity'] as
        | Record<string, unknown>
        | undefined;
      const readBig = (value: unknown): bigint | null =>
        typeof value === 'string' && /^(0|[1-9][0-9]*)$/.test(value)
          ? BigInt(value)
          : null;
      if (sortBy === 'mintPricePerTokenSompi') {
        return readBig(policy?.['mintPricePerTokenSompi']);
      }
      if (sortBy === 'mintedSupply') {
        return readBig(supply?.['mintedSupply']);
      }
      if (sortBy === 'mintedPercentBps') {
        const minted = readBig(supply?.['mintedSupply']);
        const max = readBig(supply?.['maxSupply']);
        return minted !== null && max !== null && max > 0n
          ? (minted * 10_000n) / max
          : null;
      }
      if (sortBy === 'mintVelocityAmount') {
        return readBig(velocity?.['mintedAmount']);
      }
      if (sortBy === 'mintVelocityPercentBps') {
        return BigInt(Number(velocity?.['mintedPercentBps'] ?? 0));
      }
      if (sortBy === 'createdAtMs') {
        const createdAtMs = item['createdAtMs'];
        return typeof createdAtMs === 'number' &&
          Number.isSafeInteger(createdAtMs)
          ? BigInt(createdAtMs)
          : null;
      }
      return readBig(supply?.['remainingMintSupply']);
    };
    const filtered = items
      .filter((item) => {
        const policy = item.mintPolicy as { policy?: string };
        if (mintPolicy !== 'any' && policy.policy !== mintPolicy) return false;
        if (mintStatus !== 'all' && item.mintStatus !== mintStatus)
          return false;
        if (wrapperStatus === 'active' && !item.wrapper) return false;
        if (wrapperStatus === 'missing' && item.wrapper) return false;
        return true;
      })
      .sort((left, right) => {
        const leftRecord = left as Record<string, unknown>;
        const rightRecord = right as Record<string, unknown>;
        if (sortBy === 'ticker') {
          const result = String(left.ticker).localeCompare(
            String(right.ticker),
          );
          return result !== 0
            ? sortDirection === 'asc'
              ? result
              : -result
            : String(left.covenantId).localeCompare(String(right.covenantId));
        }
        const leftValue = numeric(leftRecord);
        const rightValue = numeric(rightRecord);
        if (leftValue === null && rightValue !== null) return 1;
        if (leftValue !== null && rightValue === null) return -1;
        if (
          leftValue !== null &&
          rightValue !== null &&
          leftValue !== rightValue
        ) {
          const result = leftValue < rightValue ? -1 : 1;
          return sortDirection === 'asc' ? result : -result;
        }
        return String(left.covenantId).localeCompare(String(right.covenantId));
      });
    return {
      items: filtered.slice(offset, offset + limit),
      total: filtered.length,
      limit,
      offset,
      sortBy,
      sortDirection,
      mintVelocityWindow: params.get('mintVelocityWindow') || undefined,
    };
  }

  // ---- Admin console ------------------------------------------------------
  if (path === '/admin/me') {
    return {
      owner: '2222222222222222222222222222222222222222222222222222222222222222',
      roles: [
        'SYS_ADMIN',
        'LISTING_MANAGER',
        'TOKEN_METADATA_MANAGER',
        'FEE_TICKET_MANAGER',
        'MATCHER_OPERATOR',
        'WRAPPER_OPERATOR',
        'SNAPSHOT_MIGRATOR',
      ],
      isAdmin: true,
    };
  }
  if (m(/^\/admin\/user-roles\/.+$/)) {
    const walletAddress = decodeURIComponent(
      path.slice('/admin/user-roles/'.length),
    );
    return {
      walletAddress,
      roles: ['SNAPSHOT_MIGRATOR'],
      governancePrincipalId: 'custody.alpha',
      revision: 7,
      updatedAt: new Date(NOW - HOUR).toISOString(),
    };
  }
  if (m(/^\/admin\/governance-principals\/.+$/)) {
    const principalId = decodeURIComponent(
      path.slice('/admin/governance-principals/'.length),
    );
    return {
      principalId,
      displayName: 'Alpha custody',
      status: 'ACTIVE',
      revision: 3,
      updatedAt: new Date(NOW - HOUR).toISOString(),
    };
  }
  if (path === '/migration-admin/migrations') {
    return {
      items: [
        {
          ...MIGRATION_SUMMARY,
          status: 'VERIFYING',
          updatedAt: new Date(NOW - 12 * 60_000).toISOString(),
          createdByOwner: MIGRATION_ADMIN_DETAIL.createdByOwner,
          blockedReason: MIGRATION_ADMIN_DETAIL.blockedReason,
        },
      ],
      total: 1,
      writeCapabilities: { create: false },
    };
  }
  if (path === `/migration-admin/migrations/${MIGRATION_ID}`) {
    return MIGRATION_ADMIN_DETAIL;
  }
  if (
    path === `/migration-admin/migrations/${MIGRATION_ID}/deployment/pending`
  ) {
    return {
      pending: {
        walletRequestId: 'kcc20-snapshot.start-mock-preview',
        stage: 'snapshot-start',
        abandonmentAllowed: false,
        mode: 'abandon',
        blockedReason: 'Mock preview does not permit deployment mutations.',
      },
    };
  }
  if (path === '/admin/fee-ticket-root') {
    return {
      configured: true,
      enabled: true,
      rootId: '0x3f7b9e3d2a1c8f04',
      rootOwner: 'kaspatest:qpr20ffadmin00000000000000000000000000000000',
      utilityTokenId: '0x7af3c9e21b8d04f6a1c5',
      utilityTokenAmount: '500',
      discountBps: 25,
      supportedMarketIds: Array.from(
        { length: 14 },
        (_, i) => `0x${(i + 1).toString(16).padStart(8, '0')}`,
      ),
    };
  }
  if (path === '/admin/listings') {
    const rows: [
      string,
      string,
      string,
      boolean,
      boolean,
      boolean,
      boolean,
      string,
    ][] = [
      ['Kasper', 'KASPER', COV, true, false, true, true, 'none'],
      ['Nacho', 'NACHO', SECOND_COV, true, true, false, false, 'none'],
      [
        'Kango',
        'KANGO',
        'bb22'.padEnd(64, '0'),
        true,
        true,
        false,
        false,
        'none',
      ],
      [
        'Ghoad',
        'GHOAD',
        'cc33'.padEnd(64, '0'),
        true,
        false,
        false,
        false,
        'medium',
      ],
      [
        'Kekw',
        'KEKW',
        'dd44'.padEnd(64, '0'),
        true,
        false,
        false,
        false,
        'medium',
      ],
      [
        'Worty',
        'WORTY',
        'ee55'.padEnd(64, '0'),
        false,
        false,
        true,
        false,
        'high',
      ],
    ];
    const items = rows.map((r) => ({
      covenantId: r[2],
      ticker: r[1],
      name: r[0],
      listed: r[3],
      hidden: r[4],
      verified: r[5],
      featured: r[6],
      riskLevel: r[7],
      riskFlags: r[7] === 'none' ? [] : ['liquidity'],
      updatedByOwner: 'kaspatest:qpr20ffadmin',
      updatedByWalletAddress: 'kaspatest:qpr20ffadmin',
      history: [],
    }));
    return { items, total: items.length };
  }
  if (path === '/admin/wrappers/jobs') {
    const jobs: [string, string, number, string][] = [
      ['ZEAL', 'retry', 2, 'reserve UTXO not found'],
      ['WORTY', 'failed', 5, 'builder preflight failed'],
      ['KASPY', 'pending', 0, '—'],
    ];
    const items = jobs.map((j, i) => ({
      canonicalCovenantId: `${j[0]}`.padEnd(64, '0'),
      wrapperId: `0x${(i + 1).toString(16)}wrapper`,
      status: j[1],
      reason: j[3],
      retryCount: j[2],
      updatedByOwner: 'kaspatest:qpr20ffadmin',
      updatedByWalletAddress: 'kaspatest:qpr20ffadmin',
      history: [],
      updatedAt: new Date(NOW - i * 9 * 60_000).toISOString(),
    }));
    return { items, total: items.length };
  }
  if (path === '/admin/matcher/status') {
    return {
      config: {
        enabled: false,
        dryRun: true,
        paused: true,
        maxRetries: 5,
        maxFeeSompi: '200000000',
        minSpreadSompi: '400000',
        allowedMarketIds: [],
      },
      queueDepth: 12,
      crossedPairCount: 1,
      crossedPairs: [],
    };
  }
  if (path === '/admin/matcher/queue') {
    return { total: 0, jobs: [] };
  }
  if (path === '/admin/ops') {
    return { items: [], total: 0 };
  }
  const migrationTradingSetupOperationPath = `migration-trading-setup%3A${MIGRATION_ID}`;
  if (
    path ===
      `/migration-admin/migrations/${MIGRATION_ID}/operations/${migrationTradingSetupOperationPath}` ||
    path === `/admin/ops/${migrationTradingSetupOperationPath}`
  ) {
    return {
      operationId: `migration-trading-setup:${MIGRATION_ID}`,
      action: 'migration.trading-setup',
      status: 'projection_pending',
      wallet: 'kaspatest:qpr20ffadmin',
      ownerIdentifier:
        '1111111111111111111111111111111111111111111111111111111111111111',
      canonicalTokenId: COV,
      wrapperId: MIGRATION_WRAPPER_ID,
      projectionStatus: 'projection_pending',
      metadata: {
        operation: 'snapshot-migration-trading-setup',
        contract: 'KCC20Wrapper',
        migrationId: MIGRATION_ID,
        migrationDocumentId: MIGRATION_ID,
        tradingSetupStatus: 'PENDING',
        wrapperJobStatus: 'queued',
        orderbookId: MIGRATION_ORDERBOOK_ID,
      },
      stepTimestamps: {
        projectionPendingAt: new Date(NOW - 8 * 60_000).toISOString(),
      },
      history: [
        {
          status: 'projection_pending',
          action: 'migration.trading-setup',
          at: new Date(NOW - 8 * 60_000).toISOString(),
          builderKey: 'migration.trading-setup',
        },
      ],
      createdAt: new Date(NOW - 8 * 60_000).toISOString(),
      updatedAt: new Date(NOW - 8 * 60_000).toISOString(),
    };
  }
  if (path === '/wallet-operations/pskt-builders') {
    const b = (
      key: string,
      operation: string,
      contract: string,
      action: string,
      status = 'ready-to-build',
    ) => ({
      builderKey: key,
      operation,
      contract,
      action,
      status,
      requiredCapabilities: [`backend-funded-pskt-builder:${key}`],
      requiredSources: [],
    });
    return [
      b(
        'kcc20-snapshot.deploy-bootstrap',
        'deploy-snapshot-bootstrap',
        'KCC20SnapshotBootstrap',
        'deploy',
      ),
      b(
        'kcc20-snapshot.deploy-controller',
        'deploy-snapshot-controller',
        'KCC20SnapshotController',
        'deploy',
      ),
      b(
        'kcc20-snapshot.start',
        'start-snapshot-migration',
        'KCC20SnapshotBootstrap',
        'start',
      ),
      b(
        'kcc20-snapshot.claim',
        'snapshot.claim',
        'snapshot-controller',
        'claim',
      ),
      b('kcc20.deploy-token', 'deploy-token', 'KCC20', 'deploy'),
      b('kcc20.mint', 'mint-token', 'KCC20', 'mint'),
      b('kcc20.transfer', 'transfer-token', 'KCC20', 'transfer'),
      b('kcc20wrapper.wrap', 'wrap', 'KCC20Wrapper', 'wrap'),
      b(
        'kcc20wrapper.deploy-market',
        'deploy-wrapper-market',
        'KCC20Wrapper',
        'deployMarket',
      ),
      b('kcc20wrapper.unwrap', 'unwrap', 'KCC20Wrapper', 'unwrap'),
      b(
        'kcc20orderbook.fill-ask',
        'fill-ask-order',
        'KCC20Orderbook',
        'fillAsk',
      ),
      b(
        'kcc20orderbook.fill-bid',
        'fill-bid-order',
        'KCC20Orderbook',
        'fillBid',
      ),
      b(
        'kcc20orderbook.matcher-settle-crossed',
        'settle-crossed-orders',
        'KCC20Orderbook',
        'settleCrossed',
      ),
      b(
        'fee-ticket.create-from-utility-burn',
        'create-fee-ticket',
        'FeeTicket',
        'createFromUtilityBurn',
      ),
      b('fee-ticket.burn', 'burn-fee-ticket', 'FeeTicket', 'burn'),
    ];
  }
  return undefined;
}
